import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  IconButton,
  Switch,
} from "@mui/material";

import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridRowsProp,
} from "@mui/x-data-grid";

import { LoadingScreen } from "../../ui/components/LoadingScreen";
import {
  createTicketValidatorUser,
  deleteTicketValidatorUser,
  updateTicketValidatorUser,
} from "../../api/providers/plannerEventsProviders";
import CustomWarningModal from "../public/CustomWarningModal";
import CustomEditModal from "../public/CustomEditModal";
import { Delete, Edit } from "@mui/icons-material";

interface TicketValidator {
  id: number;
  username: string;
  password?: string;
  status: boolean;
}

interface CurrentUserData {
  username: string;
  password?: string;
  status: boolean;
}

interface PlannerEventCodeScannersProps {
  ticketValidators: TicketValidator[] | undefined;
  eventId: number;
  selectedEventData: () => void;
}

export const PlannerEventCodeScanners: React.FC<PlannerEventCodeScannersProps> = ({
  ticketValidators,
  eventId,
  selectedEventData,
}) => {
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [originalPassword, setOriginalPassword] = useState("");
  const [status, setStatus] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState();
  const [selectedUserData, setSelectedUserData] = useState<CurrentUserData>({
    username: "",
    password: "",
    status: false,
  });
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateValidator, setShowCreateValidator] = useState(false);

  const handleCreateUser = async () => {
    try {
      setLoading(true);
      const result = await createTicketValidatorUser({
        eventId: eventId,
        username: username,
        password: password,
        status: status,
      });
      if (result.ok) {
        setShowCreateValidator(false);
        selectedEventData();
      }
    } catch (error) {
      console.error("Error creando el validador:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      setLoading(true);
      const result = await deleteTicketValidatorUser(selectedUserId);
      if (result.ok) {
        setShowWarningModal(false);
        selectedEventData();
      }
    } catch (error) {
      console.error("Error obteniendo los datos del usuario:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    try {
      setLoading(true);

      const updatedData = {
        ...selectedUserData,
        ...(selectedUserData.password !== originalPassword && {
          password: selectedUserData.password,
        }),
      };

      const result = await updateTicketValidatorUser(
        selectedUserId,
        updatedData
      );
      if (result.ok) {
        setShowWarningModal(false);
        selectedEventData();
      }
    } catch (error) {
      console.error("Error obteniendo los datos del usuario:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id: number) => {
    setSelectedUserId(id);
    setShowWarningModal(true);
  };

  const handleEditClick = (
    id: number,
    username: string,
    password: string,
    status: boolean
  ) => {
    setSelectedUserId(id);
    setOriginalPassword(password);
    setSelectedUserData({ username, password, status });
    setShowEditModal(true);
  };

  const columns: GridColDef[] = [
    { field: "username", headerName: "Username", width: 200 },
    { field: "password", headerName: "Password", width: 200 },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridCellParams) => (
        <Switch checked={params.value} color="primary" />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <>
          <IconButton

            onClick={() =>
              handleEditClick(
                +params.id,
                params.row.username,
                params.row.password,
                params.row.status
              )
            }
          >
            <Edit />
          </IconButton>
          <IconButton

            onClick={() => handleDeleteClick(+params.id)}
          >
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  const rows: GridRowsProp =
    ticketValidators?.map((validator) => ({
      id: validator.id,
      username: validator.username,
      password: validator.password,
      status: validator.status,
    })) || [];

  const handleEditUserData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = event.target;
    console.log(name, value, checked, type);
    setSelectedUserData((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCloseWarningModal = () => {
    return setShowWarningModal(false);
  };

  const handleCloseEditModal = () => {
    return setShowEditModal(false);
  };

  const handleCancelButton = () => {
    return setShowCreateValidator(false);
  };

  return (
    <Box>
      {loading && <LoadingScreen />}

      <Grid container alignItems="center">
        {showCreateValidator ? (
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Crear nuevo usuario
          </Typography>
        ) : (
          <Grid item>
            <Button
              variant="contained"
              sx={{
                bgcolor: "primary.light",
                borderRadius: 1,
                my: 1,
                width: 200,
              }}
              onClick={() => setShowCreateValidator(true)}
            >
              Agregar usuario
            </Button>
          </Grid>
        )}
      </Grid>

      {showCreateValidator && (
        <>
          <Grid container spacing={2} mt={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Username"
                fullWidth
                variant="outlined"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />

              <TextField
                label="Password"
                fullWidth
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mt: 2 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Switch
                checked={status}
                onChange={(e) => setStatus(e.target.checked)}
                color="primary"
                sx={{ mt: 4 }}
              />
              <Typography variant="body1">Activo</Typography>
            </Grid>
            <Grid item>
              <Button
                color="secondary"
                variant="contained"
                sx={{ borderRadius: 1, mr: 2 }}
                onClick={() => handleCreateUser()}
              >
                Guardar
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{ borderRadius: 1 }}
                onClick={handleCancelButton}
              >
                Cancelar
              </Button>
            </Grid>
          </Grid>
        </>
      )}

      <Box sx={{ height: "60vh", width: "100%", mt: 1 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          sx={{ padding: "10px" }}
          localeText={{
            noRowsLabel: "No hay filas",
            noResultsOverlayLabel: "No se encontraron resultados.",
            toolbarColumns: "Columnas",
            toolbarColumnsLabel: "Selecciona columnas",
            toolbarFilters: "Filtros",
            toolbarFiltersLabel: "Mostrar filtros",
            toolbarFiltersTooltipHide: "Ocultar filtros",
            toolbarFiltersTooltipShow: "Mostrar filtros",
            filterPanelAddFilter: "Agregar filtro",
            filterPanelRemoveAll: "Eliminar todos",
            filterPanelDeleteIconLabel: "Eliminar",
            filterPanelLogicOperator: "Operador lógico",
            filterPanelOperatorAnd: "Y",
            filterPanelOperatorOr: "O",
            filterPanelColumns: "Columnas",
            filterPanelInputLabel: "Valor",
            filterPanelInputPlaceholder: "Filtrar valor",
            toolbarQuickFilterPlaceholder: "Buscar",
            toolbarExport: "Exportar",
            toolbarExportLabel: "Exportar",
            toolbarExportCSV: "Descargar como CSV",
            toolbarExportPrint: "Imprimir",
            toolbarDensity: "Densidad",
            toolbarDensityLabel: "Densidad",
            toolbarDensityCompact: "Compacta",
            toolbarDensityStandard: "Estándar",
            toolbarDensityComfortable: "Cómoda",
            columnMenuLabel: "Menú",
            columnMenuShowColumns: "Mostrar columnas",
            columnMenuFilter: "Filtrar",
            columnMenuHideColumn: "Ocultar",
            columnMenuUnsort: "Desordenar",
            columnMenuSortAsc: "Orden ascendente",
            columnMenuSortDesc: "Orden descendente",
          }}
        />
      </Box>
      <CustomWarningModal
        onPressCancel={handleCloseWarningModal}
        isVisible={showWarningModal}
        onPressConfirm={handleDeleteUser}
        title="Estás seguro de eliminar este usuario?"
      />
      <CustomEditModal
        isVisible={showEditModal}
        onPressConfirm={handleUpdateUser}
        onPressCancel={handleCloseEditModal}
      >
        <Grid container spacing={2} mt={2}>
          <Grid container item xs={12} spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Username"
                name="username"
                fullWidth
                variant="outlined"
                value={selectedUserData?.username}
                onChange={handleEditUserData}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Password"
                name="password"
                fullWidth
                variant="outlined"
                value={selectedUserData?.password}
                onChange={handleEditUserData}
              />
            </Grid>
          </Grid>
          <Grid
            container
            item
            xs={12}
            justifyContent="center"
            alignItems="center"
            mt={2}
          >
            <Switch
              checked={selectedUserData?.status}
              name="status"
              onChange={handleEditUserData}
              color="primary"
            />
            <Typography variant="body1" sx={{ ml: 1 }}>
              Activo
            </Typography>
          </Grid>
        </Grid>
      </CustomEditModal>
    </Box>
  );
};
